import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import {
  useDropdown,
  useDropdownButton,
  useDropdownMenu,
  useDropdownItem,
} from './component-provider';

export interface EditorHeaderAction {
  label: string;
  onClick: () => void;
}

export interface EditorHeaderProps {
  title: string;
  actions?: EditorHeaderAction[];
  debug?: boolean;
  editable?: boolean;
  statusBadgeColor?: 'green' | 'blue';
}

export function EditorHeader({
  title,
  actions = [],
  debug = false,
  editable = true,
  statusBadgeColor = 'green',
}: EditorHeaderProps) {
  const Dropdown = useDropdown();
  const DropdownButton = useDropdownButton();
  const DropdownMenu = useDropdownMenu();
  const DropdownItem = useDropdownItem();
  const badgeColors = {
    green:
      'bg-green-50 text-green-700 inset-ring-green-600/20 dark:bg-green-500/10 dark:text-green-400 dark:inset-ring-green-500/10',
    blue: 'bg-blue-50 text-blue-700 inset-ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:inset-ring-blue-500/10',
  };

  return (
    <div className="border-b border-gray-200 dark:border-white/10">
      <div className="sm:flex sm:items-baseline sm:justify-between">
        <div className="sm:w-0 sm:flex-1 px-4 py-4">
          <h1 className="text-base font-semibold">{title}</h1>
        </div>

        <div className="mt-4 flex items-center justify-between sm:mt-0 sm:ml-6 sm:shrink-0 sm:justify-start">
          {debug &&
            (editable ? (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium inset-ring ${badgeColors[statusBadgeColor]}`}
              >
                Editable
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700">
                Read-only
              </span>
            ))}
          {debug && actions.length > 0 && (
            <div className="-my-2 ml-3 inline-block text-left">
              <Dropdown as="div" className="relative">
                <DropdownButton
                  aria-label="Menu"
                  className="flex items-center rounded-full p-2 text-gray-400 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-indigo-500 dark:hover:text-gray-300"
                >
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                </DropdownButton>

                <DropdownMenu
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg outline-1 outline-black/5 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                >
                  <div className="py-1">
                    {actions.map((action, index) => (
                      <DropdownItem key={index}>
                        <button
                          type="button"
                          onClick={action.onClick}
                          className="flex w-full justify-between px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900 data-focus:outline-hidden dark:text-gray-300 dark:data-focus:bg-white/5 dark:data-focus:text-white"
                        >
                          <span>{action.label}</span>
                        </button>
                      </DropdownItem>
                    ))}
                  </div>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
